import type { ESPolicyPx } from '../../../../../../api/policies.api.types';
import type { FieldValue } from '../../../../../../form-engine/types';
import type { QuoteFieldValue } from '../../../../../../api/quotes.api.types';
import type { TowerValue } from '../../../../../../components/tower';
import type { PrimeXQuoteState } from '../../../../quotes/prime-x/types';
import type { Nullable } from '../../../../../../types/common/utility';
import type { FirmographicDataDto } from '../../../../../../types/firmo/firmographic-data.dto';
import type { INDUSTRY_STEP_FIELDS } from '../constants';

import type { AmendatoryCoverageManager } from '../../../../quotes/prime-x/views/underwriter/components/coverages/amendatory-coverages/manager';
import type {
  CategoryCustomizations,
  UwActionBlockers,
} from '../../../../quotes/prime-x/views/underwriter/types';
import type {
  CalculateCoveragesEndorsementResponse,
  MidtermEndorsementTemplate,
  Progress,
  StepPrg,
  Templates,
} from '../api/types';

export type EndorsementState = {
  policy: ESPolicyPx;
  templates: Templates;
  progress: Progress;
};

export enum StepStatus {
  INITIALIZED = 'INITIALIZED',
  COMPLETED = 'COMPLETED',
  DELETED = 'DELETED',
}

export enum StepAttentionReason {
  FORM_SET_CHANGED = 'FORM_SET_CHANGED',
  RATER_REJECTED_CHANGE = 'RATER_REJECTED_CHANGE',
}

export function isEndorsementState(obj: any): obj is EndorsementState {
  return (
    obj != null &&
    obj.policy != undefined &&
    obj.templates != undefined &&
    obj.progress != undefined
  );
}

export type UI_Step = {
  title: string;
  isCompleted: boolean;
  key: string;
  id: string;
  attentionReason: Nullable<StepAttentionReason>;
  attentionTriggeredByStepId: Nullable<string>;
};

export type StepperStep = {
  key: string;
  title: string;
  isCurrent: boolean;
  isCompleted: boolean;
  isLoading: boolean;
  isLocked: boolean;
  needsAttention: boolean;
};

export type StepperScreenProps = {
  id: string;
  title: string;
  progress?: StepPrg;
  template?: MidtermEndorsementTemplate;
};

export interface CoverageEndorsementsState
  extends Omit<PrimeXQuoteState, 'raterX' | 'risk'> {
  raterX: CalculateCoveragesEndorsementResponse;
  policy: ESPolicyPx;
  amendatoryCoverageManager: AmendatoryCoverageManager;
  totalPremium: number;
  categoryCustomizations: CategoryCustomizations;
  payload?: UpdateCoveragesStepPayload;
  stepId: string;
  uwActionBlockersRef: React.MutableRefObject<Map<string, UwActionBlockers>>;
}

export type UpdateFormsStepPayload = {
  dynamicFields: Record<string, FieldValue>;
};

export type DetailsStepValues = {
  revenue: number;
  otherRevenue: Nullable<number>;
  noOfEmployeesAll: number;
  yearEstablished: number;
  numberOfPersonalRecords: Nullable<number>;
  dunsNumber: Nullable<string>;
};

export type UpdateDetailsStepPayload = {
  firmographicData: DetailsStepValues & { totalRevenue: number };
};

export type DomainStepValues = {
  domainName: string;
};

export type UpdateDomainStepPayload = {
  firmographicData: DomainStepValues;
};

export type IndustryStepValues = Pick<
  FirmographicDataDto,
  (typeof INDUSTRY_STEP_FIELDS)[number]
>;

export type UpdateIndustryStepPayload = {
  firmographicData: IndustryStepValues;
};

export type OwnershipStepValues = Pick<
  FirmographicDataDto,
  'ownershipType' | 'entityType'
>;

export type UpdateOwnershipStepPayload = {
  firmographicData: OwnershipStepValues;
};

export type OrgContactStepValues = Required<
  Pick<
    FirmographicDataDto,
    | 'customerFirstName'
    | 'customerLastName'
    | 'customerEmail'
    | 'customerPhoneNumber'
  >
>;

export type UpdateOrgContactStepPayload = {
  firmographicData: OrgContactStepValues;
};

export type UpdateCoveragesStepPayload = {
  endDate: string;
  dynamicFields: Record<string, QuoteFieldValue>;
  dynamicFieldsSettings: Record<string, { isPegValue: boolean }>;
  limit: number;
  deductible: number;
  waitingPeriod: number;
  retroactivePeriod?: number;
  retroactiveDate?: string;
};

/**
 * Excess (PRIME_ONE_PLUS_US) coverages-step payload. The backend template's
 * `endorsable_property_paths` for excess is `{ endDate, dynamicFields.*,
 * cowbellAttachment.limit }`, so — unlike the first-party payload — it carries
 * the tower's Cowbell attachment limit and none of the scalar global props
 * (deductible, waitingPeriod, retroactivePeriod/Date, limit). See FPX-7434.
 */
export type UpdateExcessCoveragesStepPayload = {
  endDate: string;
  dynamicFields: Record<string, QuoteFieldValue>;
  dynamicFieldsSettings: Record<string, { isPegValue: boolean }>;
  // The excess tower is endorsed via the whole `globalValue` field: the reformer
  // whitelists by top-level field name and cannot target a single tower layer, so
  // we send the full (schema-valid) tower with the edited cowbellAttachment.limit.
  globalValue: TowerValue;
};

export type ChipVariant = 'Previous' | 'New';
